import type { NestedFieldFilter, SearchField } from '@compass/client/types/case_record';
import { Loader2 } from 'lucide-react';
import React from 'react';

import type { FiltersConfig, QuickFilterConfig } from '@/config/quickFilters.config';
import { type PinnedDates, useCaseCounts } from '@/hooks/useCaseCounts';
import { cn } from '@/utils';

import { Tabs, TabsList, TabsTrigger } from './ui/tabs';

interface QuickFiltersProps {
  activeFilter: string;
  onFilterChange: (filterId: string) => void;
  /** Workflow ID to scope per-tab counts. Omit for cross-program / cross-workflow
   * views (e.g. My Cases) — badges will be hidden since `getCaseCounts` is
   * workflow-scoped and no org-wide counts API exists. Caller may instead pass
   * `counts` directly to render badges from a custom source. */
  workflowId?: string;
  /** Caller-provided counts, keyed by `QuickFilterConfig.name`. Takes precedence
   * over `useCaseCounts(workflowId, …)` when present. */
  counts?: Record<string, number>;
  countsLoading?: boolean;
  className?: string;
  filtersConfig: FiltersConfig;
  baseFilters?: {
    reference_ids?: string[];
    case_record_ids?: string[];
    case_codes?: string[];
    assigned_user_ids?: string[];
    created_at_after?: string;
    created_at_before?: string;
    campaign_id?: string;
    nested_filters?: NestedFieldFilter[];
    search_field?: SearchField;
    search_value?: string;
  };
  /** Date bounds for tabs with `hidesDatePicker` (AQ-863). */
  pinnedDates?: PinnedDates;
}

export const QuickFilters: React.FC<QuickFiltersProps> = ({
  activeFilter,
  onFilterChange,
  workflowId,
  counts: countsProp,
  countsLoading: countsLoadingProp,
  className,
  filtersConfig,
  baseFilters = {},
  pinnedDates,
}) => {
  // Get filters from the provided config
  const quickFilters: QuickFilterConfig[] = filtersConfig.filters;

  // Fetch counts for each filter using the custom hook. We thread only the fields
  // useCaseCounts needs (name + filter) so the QuickFilterConfig
  // shape doesn't leak through the hook signature. Hook is always called (rules-of-
  // hooks) but disabled internally when workflowId is empty, so cross-program views
  // pay no network cost.
  const { data: hookCounts, isLoading: hookCountsLoading } = useCaseCounts(
    workflowId ?? '',
    quickFilters.map(f => ({
      name: f.name,
      filter: f.filter,
      hidesDatePicker: f.hidesDatePicker,
    })),
    baseFilters,
    pinnedDates,
  );

  // Caller-provided counts win over hook-derived counts. When neither is available
  // (no workflowId and no countsProp), badges are hidden entirely.
  const counts = countsProp ?? hookCounts;
  const countsLoading = countsProp !== undefined ? !!countsLoadingProp : hookCountsLoading;
  const showBadges = !!workflowId || countsProp !== undefined;

  return (
    <Tabs value={activeFilter} onValueChange={onFilterChange} className={className}>
      <TabsList>
        {quickFilters.map(filterConfig => {
          const count = counts?.[filterConfig.name];
          return (
            <TabsTrigger
              key={filterConfig.id}
              value={filterConfig.id}
              className="flex items-center gap-2 text-muted-foreground data-[state=active]:text-foreground"
              aria-label={`Filter by ${filterConfig.name}${count !== undefined ? ` (${count} items)` : ''}`}
            >
              <span>{filterConfig.name}</span>
              {showBadges && (
                <span
                  className={cn(
                    'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-md border-black',
                    activeFilter === filterConfig.id
                      ? filterConfig.badgeColor || 'bg-muted text-txt-secondary'
                      : 'bg-muted text-txt-secondary',
                  )}
                >
                  {countsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : (count ?? '—')}
                </span>
              )}
            </TabsTrigger>
          );
        })}
      </TabsList>
    </Tabs>
  );
};

// Re-export types for use in parent components
